import { bech32 } from '@scure/base'
import { z } from 'zod'

/** 登録可能な mainnet の鍵由来 Base/Reward/Enterprise アドレスと同期情報。 */
export type CardanoAddress = {
  readonly address: string
} & (
  | {
      readonly type: 'base' | 'reward'
      readonly stakeAddress: string
      readonly stakeCredential: string
      readonly paymentCredential?: string
    }
  | {
      readonly type: 'enterprise'
      readonly stakeAddress: null
      readonly stakeCredential: null
      readonly paymentCredential: string
    }
)

/**
 * 公開アドレスの checksum・種別・ネットワークを検証し、同期情報を返す。
 * @param params.address 鍵由来の Shelley Base、Reward または Enterprise アドレス
 * @returns 小文字に正規化した登録元アドレスと Stake 情報（Enterprise では null）
 */
export const parseCardanoAddress = (params: {
  address: string
}): CardanoAddress => {
  const input = params.address.trim()
  // Cardano Base Address は Bech32 の既定の 90 文字制限を超える。
  const decoded = bech32.decodeUnsafe(input, 200)
  if (decoded === undefined) throw new Error('Cardano アドレスの形式が不正です')
  const bytes = bech32.fromWords(decoded.words)
  const header = bytes[0]
  if (header === undefined || (header & 15) !== 1) {
    throw new Error('Cardano mainnet のアドレスを入力してください')
  }
  const type = header >> 4
  const isBase = type === 0 && decoded.prefix === 'addr' && bytes.length === 57
  const isReward =
    type === 14 && decoded.prefix === 'stake' && bytes.length === 29
  if (type === 6 && decoded.prefix === 'addr' && bytes.length === 29) {
    return {
      address: input.toLowerCase(),
      type: 'enterprise',
      stakeAddress: null,
      stakeCredential: null,
      paymentCredential: hexFromBytes({ bytes: bytes.slice(1) }),
    }
  }
  if (!isBase && !isReward) {
    throw new Error(
      '鍵由来の Shelley Base / Stake / Enterprise Address のみ登録できます',
    )
  }
  const stakeBytes = bytes.slice(isBase ? 29 : 1)
  const rewardBytes = new Uint8Array(29)
  rewardBytes[0] = 0xe1
  rewardBytes.set(stakeBytes, 1)
  return {
    address: input.toLowerCase(),
    type: isBase ? 'base' : 'reward',
    stakeAddress: bech32.encode('stake', bech32.toWords(rewardBytes), 200),
    stakeCredential: hexFromBytes({ bytes: stakeBytes }),
    paymentCredential: isBase
      ? hexFromBytes({ bytes: bytes.slice(1, 29) })
      : undefined,
  }
}

/**
 * アドレス検証を登録フォームと API の実行時検証で共有する。
 * @returns 正規化した Cardano アドレス情報を出力する Zod schema
 */
export const cardanoAddressSchema = z.string().transform((address, context) => {
  try {
    return parseCardanoAddress({ address })
  } catch {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Cardano mainnet の鍵由来 Base / Stake / Enterprise Address を入力してください',
    })
    return z.NEVER
  }
})

/**
 * 探索した Payment Address が同期対象の通常の鍵由来 Base Address か判定する。
 * Stake credential 一致は payment key の所有者一致を証明しない点に注意する。
 * @param params.address 探索した公開アドレス
 * @param params.stakeAddress 登録済み同期キー
 * @returns Script-payment 等を除外し、同じ Stake Credential を持つ場合のみ true
 */
export const isOwnedCardanoPaymentAddress = (params: {
  address: string
  stakeAddress: string
}): boolean => {
  const parsed = cardanoAddressSchema.safeParse(params.address)
  return (
    parsed.success &&
    parsed.data.type === 'base' &&
    parsed.data.stakeAddress === params.stakeAddress
  )
}

/**
 * credential のバイト列を小文字 hexadecimal に変換する。
 * @param params.bytes credential のバイト列
 * @returns 固定幅の hexadecimal 文字列
 */
const hexFromBytes = (params: { bytes: Uint8Array }): string =>
  Array.from(params.bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  )

/**
 * mainnetのScript-payment Base/Enterprise Addressを検証する。
 * 登録可能な自己保有アドレスとは区別し、未対応形式はfalseとする。
 * @param params.address UTxOの入力先アドレス
 * @returns 支払CredentialがScriptの対応形式ならtrue
 */
export const isCardanoScriptPaymentAddress = (params: {
  address: string
}): boolean => {
  const decoded = bech32.decodeUnsafe(params.address, 200)
  if (decoded === undefined || decoded.prefix !== 'addr') return false
  const bytes = bech32.fromWordsUnsafe(decoded.words)
  if (bytes === undefined) return false
  const header = bytes[0]
  if (header === undefined || (header & 15) !== 1) return false
  const type = header >> 4
  return (
    ((type === 1 || type === 3) && bytes.length === 57) ||
    (type === 7 && bytes.length === 29)
  )
}
